/*
 * Maxilite consent logger (hardcoded).
 *
 * Records each real cookie/consent decision (Shopify's "visitorConsentCollected"
 * event) to the durable proof-of-consent log, so consent history can be exported
 * for a privacy inspection beyond Shopify's native 30-day window.
 *
 * Works alongside the custom banner (assets/cookie-consent.js): the banner calls
 * setTrackingConsent(), which fires the event this listener records.
 *
 * Delivery goes through the Maxilite Hub App Proxy (/apps/hub-proxy/consent):
 * same-origin so ad blockers don't eat it, and Shopify signs the request so the
 * app can verify it came through this storefront. The app forwards the record
 * to the same Convex consentLogs store as before. On local dev (shopify theme
 * dev at 127.0.0.1) the /apps/ proxy doesn't exist, so the logger falls back to
 * POSTing the Convex endpoint directly.
 *
 * Debug (testing): add ?cookie_debug=1 to the URL, set localStorage.cookieConsentDebug='1',
 * or window.COOKIE_CONSENT_DEBUG=true to log each consent event + send to the console.
 */
(function () {
  'use strict';

  // ---- Hardcoded config -----------------------------------------------------
  var ENDPOINT = '/apps/hub-proxy/consent'; // Maxilite Hub App Proxy (signed, same-origin)
  // Direct Convex ingest — used only as the local-dev fallback where /apps/ doesn't resolve.
  var DIRECT_ENDPOINT = 'https://perceptive-avocet-624.convex.site/api/consent';
  var BANNER_VERSION = '1.0'; // bump when the consent banner / cookie policy text changes
  // ---------------------------------------------------------------------------

  var DEBUG = (function () {
    try {
      if (/[?&]cookie_debug=1/.test(window.location.search)) return true;
      if (window.localStorage && localStorage.getItem('cookieConsentDebug') === '1') return true;
    } catch (e) {}
    return window.COOKIE_CONSENT_DEBUG === true;
  })();

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[consent-logger]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // Local-dev / debug endpoint override. The /apps/ proxy path only exists on the
  // real storefront domain, so on a local dev host (shopify theme dev at
  // 127.0.0.1/localhost) fall back to the direct Convex endpoint. Explicit override
  // via localStorage.cookieConsentEndpoint or ?cookie_endpoint=<url> always wins.
  var isLocalDev = /^(?:localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  if (DEBUG || isLocalDev) {
    try {
      var override = (window.localStorage && localStorage.getItem('cookieConsentEndpoint')) || '';
      var epMatch = window.location.search.match(/[?&]cookie_endpoint=([^&]+)/);
      if (epMatch) override = decodeURIComponent(epMatch[1]);
      if (!override && isLocalDev) override = DIRECT_ENDPOINT;
      if (override) {
        ENDPOINT = override;
        log('endpoint =', ENDPOINT);
      }
    } catch (e) {}
  }

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

  var lastSignature = null;
  var pendingAction = null; // how consent was given, captured from the banner click

  // Attach immediately so no consent decision is missed. The event is dispatched
  // on `document` and does not require the API to be loaded first.
  document.addEventListener('visitorConsentCollected', function (event) {
    try {
      handle(event && event.detail);
    } catch (e) {
      log('handler error', e);
    }
  });

  // Fallback: the banner dispatches this on the actual click, so logging works even when
  // Shopify's visitorConsentCollected does not fire (e.g. `shopify theme dev` over HTTP).
  // Deferred so the canonical event (which carries consentId) wins when it does fire.
  document.addEventListener('maxiliteConsentUpdated', function (event) {
    var detail = event && event.detail;
    // Capture the action synchronously so it's attached even when the canonical
    // visitorConsentCollected event (which has no action) is what actually sends.
    if (detail && detail.action) pendingAction = detail.action;
    setTimeout(function () {
      try {
        handle(detail);
      } catch (e) {
        log('handler error', e);
      }
    }, 1200);
  });

  // Ensure the Customer Privacy API is present for consentId()/getRegion() reads.
  try {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {});
  } catch (e) {
    /* the listener above still works if the banner loads the API itself */
  }

  // EU/EEA + UK country codes, used to derive a coarse regulatory scope label.
  var EU_EEA_UK = {
    AT: 1, BE: 1, BG: 1, HR: 1, CY: 1, CZ: 1, DK: 1, EE: 1, FI: 1, FR: 1, DE: 1, GR: 1,
    HU: 1, IE: 1, IT: 1, LV: 1, LT: 1, LU: 1, MT: 1, NL: 1, PL: 1, PT: 1, RO: 1, SK: 1,
    SI: 1, ES: 1, SE: 1, IS: 1, LI: 1, NO: 1, GB: 1
  };

  // Best-effort regulatory label from the visitor's region (raw region is also stored).
  function deriveScope(region, saleOfDataRegion) {
    var r = (region || '').toUpperCase();
    var country = r.slice(0, 2);
    if (country === 'CA') return r.slice(2, 4) === 'QC' ? 'quebec' : 'canada';
    if (country === 'US') return saleOfDataRegion ? 'ccpa' : 'us';
    if (EU_EEA_UK[country]) return 'gdpr';
    if (saleOfDataRegion) return 'ccpa';
    return r ? 'other' : 'unknown';
  }

  // Map Shopify's 'yes' | 'no' | '' consent strings to a tri-state boolean/null.
  function bool(v) {
    if (v === true || v === false) return v;
    if (v === 'yes') return true;
    if (v === 'no') return false;
    return null;
  }

  // Read a value that may be exposed as a method or a property, without throwing.
  function invoke(obj, name) {
    try {
      if (!obj) return null;
      var v = obj[name];
      if (typeof v === 'function') return v.call(obj);
      return v == null ? null : v;
    } catch (e) {
      return null;
    }
  }

  function handle(detail) {
    var cp = (window.Shopify && window.Shopify.customerPrivacy) || {};
    var current = invoke(cp, 'currentVisitorConsent') || {};

    function pick(detailKey, currentKey) {
      if (detail) {
        if (detailKey in detail) return !!detail[detailKey]; // Shopify event shape: analyticsAllowed…
        if (currentKey in detail) return !!detail[currentKey]; // our click event shape: analytics…
      }
      return bool(current[currentKey]);
    }

    var consentId = invoke(cp, 'consentId');
    var region = invoke(cp, 'getRegion');
    var saleOfDataRegion = invoke(cp, 'saleOfDataRegion');
    var analytics = pick('analyticsAllowed', 'analytics');
    var marketing = pick('marketingAllowed', 'marketing');
    var preferences = pick('preferencesAllowed', 'preferences');
    var saleOfData = pick('saleOfDataAllowed', 'sale_of_data');

    // De-dupe on the 4 flags only (not consentId) so the canonical event and the
    // click fallback collapse to a single log within this page session.
    var signature = [analytics, marketing, preferences, saleOfData].join('|');
    if (signature === lastSignature) {
      log('duplicate consent event ignored');
      return;
    }
    lastSignature = signature;

    var payload = {
      consentId: consentId,
      region: region,
      analytics: analytics,
      marketing: marketing,
      preferences: preferences,
      sale_of_data: saleOfData,
      action: (detail && detail.action) || pendingAction || null,
      scope: deriveScope(region, saleOfDataRegion),
      policyVersion: BANNER_VERSION,
      shop: (window.Shopify && window.Shopify.shop) || null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ts: new Date().toISOString()
    };

    log('sending consent ->', ENDPOINT, payload);
    deliver(JSON.stringify(payload));
  }

  function deliver(body) {
    if (window.fetch) {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: body,
        keepalive: true
      })
        .then(function (res) {
          log('POST complete, status', res && res.status);
        })
        .catch(function (err) {
          log('POST failed, falling back to sendBeacon', err);
          beacon(body);
        });
    } else {
      beacon(body);
    }
  }

  function beacon(body) {
    try {
      if (navigator.sendBeacon) {
        var ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        log('sendBeacon queued:', ok);
      }
    } catch (e) {
      log('sendBeacon error', e);
    }
  }
})();
